import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { mergeRegister } from '@lexical/utils'
import { $getSelection, $isRangeSelection, $getRoot, CAN_REDO_COMMAND, CAN_UNDO_COMMAND, REDO_COMMAND, UNDO_COMMAND } from 'lexical'
import { useContext, useEffect, useRef, useState, useCallback } from 'react'

import { AppContext } from '../App'
import { fetchVoices, setDrilldownLevel, setIsCursorAtStart, setFullscreen } from '.'
import { useAsyncError } from '../errorBoundary/useAsyncError'
import { Config } from '../utils/config'
import backIcon from 'url:../../assets/icons/back.svg'
import playAudioIcon from 'url:../../assets/icons/play.svg'
import stopAudioIcon from 'url:../../assets/icons/stop.svg'
import insertSsmlIcon from 'url:../../assets/icons/insert.svg'
import pasteIcon from 'url:../../assets/icons/paste.svg'
import fullscreenEnterIcon from 'url:../../assets/icons/fullscreenEnter.svg'
import fullscreenExitIcon from 'url:../../assets/icons/fullscreenExit.svg'

const Audio = window.Audio
const LowPriority = 1
const MAX_CHAR_COUNT = process.env.MAX_CHAR_COUNT || 900

const SSML_ITEMS = [
  { symbol: '💬', label: 'Voices', drillLv: 'gender' },
  { symbol: '⏸️', label: 'Pause', drillLv: 'pause' },
  { symbol: '📢', label: 'Emphasis', drillLv: 'emphasis' },
  { symbol: '🤫', label: 'Prosody', drillLv: 'prosody' }
]

const GENDER_OPTIONS = [
  { symbol: '👨', label: 'Male', value: 'm' },
  { symbol: '👩', label: 'Female', value: 'f' }
]

const LANGUAGE_OPTIONS = [
  { symbol: '🇺🇸', label: 'English (US)', value: 'en-US' },
  { symbol: '🇬🇧', label: 'English (UK)', value: 'en-GB' }
]

const PAUSE_OPTIONS = [
  { label: '—', value: '', description: 'time: 500ms, strength: medium' },
  { label: '(3s)', value: '(3s)' },
  { label: '(700ms)', value: '(700ms)' },
  { label: '(700ms, x-weak)', value: '(700ms, x-weak)' },
  { label: '(700ms, weak)', value: '(700ms, weak)' },
  { label: '(700ms, medium)', value: '(700ms, medium)' },
  { label: '(700ms, strong)', value: '(700ms, strong)' },
  { label: '(700ms, x-strong)', value: '(700ms, x-strong)' },
  { label: '(700ms, none)', value: '(700ms, none)' }
].map((option) => ({
  symbol: '⏸️',
  ...option
}))

const EMPHASIS_OPTIONS = [
  { label: '—', value: '', description: 'level: none' },
  { label: '(strong)', value: '(strong)' },
  { label: '(moderate)', value: '(moderate)' },
  { label: '(reduced)', value: '(reduced)' }
].map((option) => ({
  symbol: '📢',
  ...option
}))

const PROSODY_OPTIONS = [
  { label: '—', value: '', description: 'rate: default, pitch: default, volume: default' },
  { label: '(+15.2%)', value: '(+15.2%)' },
  { label: '(x-slow)', value: '(x-slow)' },
  { label: '(slow)', value: '(slow)' },
  { label: '(fast)', value: '(fast)' },
  { label: '(x-fast)', value: '(x-fast)' },
  { label: '(default, +10Hz)', value: '(default, +10Hz)' },
  { label: '(default, -5.5Hz)', value: '(default, -5.5Hz)' },
  { label: '(default, +0.5st)', value: '(default, +0.5st)' },
  { label: '(default, +5st)', value: '(default, +5st)', description: 'semitones' },
  { label: '(default, x-low)', value: '(default, x-low)' },
  { label: '(default, low)', value: '(default, low)' },
  { label: '(default, medium)', value: '(default, medium)' },
  { label: '(default, high)', value: '(default, high)' },
  { label: '(default, x-high)', value: '(default, x-high)' },
  { label: '(default, default, +6.0dB)', value: '(default, default, +6.0dB)' },
  { label: '(default, default, -0dB)', value: '(default, default, -0dB)' },
  { label: '(default, default, -6.0dB)', value: '(default, default, -6.0dB)' },
  { label: '(default, default, silent)', value: '(default, default, silent)' },
  { label: '(default, default, x-soft)', value: '(default, default, x-soft)' },
  { label: '(default, default, soft)', value: '(default, default, soft)' },
  { label: '(default, default, medium)', value: '(default, default, medium)' },
  { label: '(default, default, loud)', value: '(default, default, loud)' },
  { label: '(default, default, x-loud)', value: '(default, default, x-loud)' },
  { label: '(default, default, default)', value: '(default, default, default)', description: '+0.0dB' },
  { label: '(slow, low, x-soft)', value: '(slow, low, x-soft)' },
  { label: '(+15.2%, +10Hz, +6.0dB)', value: '(+15.2%, +10Hz, +6.0dB)' },
  { label: '(+15.2%, +2st, -1.0dB)', value: '(+15.2%, +2st, -1.0dB)' },
  { label: '(default, -3st, x-soft)', value: '(default, -3st, x-soft)' }
].map((option) => ({
  symbol: '🤫',
  ...option
}))

const ToolbarPlugin = () => {
  const { state, dispatch } = useContext(AppContext)
  const [editor] = useLexicalComposerContext()
  const toolbarRef = useRef(null)

  const [selectedGender, setSelectedGender] = useState(null)
  const [selectedLanguage, setSelectedLanguage] = useState(null)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const [charCount, setCharCount] = useState(MAX_CHAR_COUNT)
  const throwAsyncError = useAsyncError()

  const [currentVoice, setCurrentVoice] = useState(null)
  const [audio, setAudio] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)

  function isSelectionInFirstParagraph (selection, targetKey) {
    if (!selection || !selection.anchor) return false

    const root = $getRoot()

    const result = root
      ?.getChildren?.()?.[0]
      ?.getChildren?.()?.[0]
      ?.__key === targetKey

    if (!result) return false

    return true
  }

  useEffect(() => {
    fetchVoices(dispatch, throwAsyncError)
  }, [])

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(CAN_UNDO_COMMAND, (payload) => {
        setCanUndo(payload)
        return false
      }, LowPriority),
      editor.registerCommand(CAN_REDO_COMMAND, (payload) => {
        setCanRedo(payload)
        return false
      }, LowPriority)
    )
  }, [editor])

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (toolbarRef.current && !toolbarRef.current.contains(event.target)) {
        dispatch(setDrilldownLevel(null))
        stopAudio()
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [audio])

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot()
        const plainText = root.getTextContent()
        setCharCount(MAX_CHAR_COUNT - plainText.length)
      })
    })
  }, [editor])

  const handlePaste = async () => {
    editor.focus()
    const text = await navigator.clipboard.readText()
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        selection.insertText(text)
      }
    })
  }

  const toggleFullscreen = () => {
    dispatch(setFullscreen(!state.editor.isFullscreen))
    !state.editor.isFullscreen
      ? document.body.classList.add('body-no-scroll')
      : document.body.classList.remove('body-no-scroll')
  }

  const getFilteredVoices = () => {
    let voices = state.voices.filter(voice => voice.gender === selectedGender && voice.language === selectedLanguage)
    if (state.editor.isCursorAtStart === true) {
      voices = voices.filter(voice => !voice.name.includes('Wavenet'))
    }

    return voices.map(voice => voice.name)
  }

  const insertSsml = (text, ssmlItem) => {
    const symbol = ssmlItem === 'Voices'
      ? LANGUAGE_OPTIONS.find(option => option.value === text.substring(1, 6))?.symbol || ''
      : SSML_ITEMS.find(item => item.label === ssmlItem)?.symbol || ''

    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        selection.insertText(` ${symbol}${text} `)
      }
    })

    dispatch(setDrilldownLevel(null))
  }

  const stopAudio = () => {
    audio?.pause()
    setIsPlaying(false)
    setCurrentVoice(null)
  }

  const handlePlayStopAudio = (voice) => {
    const isCurrentlyPlaying = currentVoice === voice && isPlaying
    if (isCurrentlyPlaying) {
      stopAudio()
    } else {
      audio?.pause()
      const newAudio = new Audio(`${Config.BASE_URL}/voice_samples/${voice}.m4a`)
      newAudio.play()
      newAudio.addEventListener('ended', () => {
        setIsPlaying(false)
        setCurrentVoice(null)
      })

      setAudio(newAudio)
      setIsPlaying(true)
      setCurrentVoice(voice)
    }
  }

  const renderVoiceItems = () =>
    getFilteredVoices().map((voice) => (
      <div key={voice} className='item'>
        <button className='icon' onClick={() => handlePlayStopAudio(voice)} type='button'>
          <img src={currentVoice === voice && isPlaying ? stopAudioIcon : playAudioIcon} />
        </button>
        <button onClick={() => insertSsml(`(${voice})`, 'Voices')} type='button'>
          <span className='text'>({voice})</span>
        </button>
      </div>
    ))

  const renderBackButton = useCallback((label, onClick) => (
    <button className='item' onClick={() => { stopAudio(); onClick() }} type='button'>
      <img src={backIcon} className='icon' />
      <span className='text'>{label}</span>
    </button>
  ), [audio])

  const renderDrilldownItems = (items, onBackClick, onItemClick) => {
    return (
      <div className='dropdown'>
        {onBackClick && renderBackButton('Back', onBackClick)}
        {items.map((item, index) => (
          <button
            key={item.label}
            className='item'
            onClick={() => onItemClick(item)}
            type='button'
          >
            <span className='icon'>{item.symbol}</span>
            <div className='flex flex-col text text-left'>
              <span className='text'>{item.label || '\u00A0'}</span>
              <span className='description'>{item.description}</span>
            </div>
          </button>
        ))}
      </div>
    )
  }

  const drilldownHandlers = (level) => {
    switch (level) {
      case 'ssml':
        return renderDrilldownItems(SSML_ITEMS, null, (item) => { dispatch(setDrilldownLevel((item.drillLv))) })

      case 'gender':
        return renderDrilldownItems(GENDER_OPTIONS, () => dispatch(setDrilldownLevel('ssml')), (gender) => {
          setSelectedGender(gender.value)
          dispatch(setDrilldownLevel('language'))
        })

      case 'language':
        return renderDrilldownItems(LANGUAGE_OPTIONS, () => dispatch(setDrilldownLevel('gender')), (language) => {
          setSelectedLanguage(language.value)
          dispatch(setDrilldownLevel('voices'))
        })

      case 'voices':
        return (<div className='dropdown'> {renderBackButton('Back', () => dispatch(setDrilldownLevel('language')))} {renderVoiceItems()} </div>)

      case 'pause':
        return renderDrilldownItems(PAUSE_OPTIONS, () => dispatch(setDrilldownLevel('ssml')), (pause) => insertSsml(pause.value, 'Pause'))

      case 'emphasis':
        return renderDrilldownItems(EMPHASIS_OPTIONS, () => dispatch(setDrilldownLevel('ssml')), (emphasis) => insertSsml(emphasis.value, 'Emphasis'))

      case 'prosody':
        return renderDrilldownItems(PROSODY_OPTIONS, () => dispatch(setDrilldownLevel('ssml')), (prosody) => insertSsml(prosody.value, 'Prosody'))

      default:
        return null
    }
  }

  return (
    <div className='toolbar' ref={toolbarRef}>
      <button
        disabled={!canUndo}
        onClick={() => editor.dispatchCommand(UNDO_COMMAND)}
        className='toolbar-item mr-2' aria-label='Undo'
        type='button'
      >
        <i className='format undo' />
      </button>

      <button
        disabled={!canRedo}
        onClick={() => editor.dispatchCommand(REDO_COMMAND)}
        className='toolbar-item mr-2' aria-label='Redo'
        type='button'
      >
        <i className='format redo' />
      </button>

      <button
        disabled={!state.editor.isUserActiveInEditor}
        onClick={handlePaste}
        className={`toolbar-item mr-2 flex items-center ${!state.editor.isUserActiveInEditor ? 'opacity-50 cursor-not-allowed' : ''}`}
        type='button'
      >
        <img className='mr-1 w-4 h-4' src={pasteIcon} />
      </button>

      <button
        className={`toolbar-item mr-2 flex items-center ${!state.editor.isUserActiveInEditor ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={
          () => {
            editor.read(() => {
              const editorState = editor.getEditorState()
              const selection = editorState._selection
              const isEditorEmpty = $getRoot().getTextContent().trim() === ''

              if (isEditorEmpty) {
                dispatch(setIsCursorAtStart(true))
                return
              }

              if (!isSelectionInFirstParagraph(selection, selection.anchor.key)) {
                dispatch(setIsCursorAtStart(false))
                return
              }

              let atTheBeginning = false
              const textBeforeCursor = selection.anchor.getNode()
                .getTextContent()
                .substring(0, selection.focus.offset)
                .trim()

              if (Math.abs(selection.focus.offset - selection.anchor.offset) === 0) {
                atTheBeginning = textBeforeCursor.length === 0
              } else {
                const selectedText = selection.anchor.getNode()
                  .getTextContent()
                  .substring(selection.anchor.offset, selection.focus.offset)

                const indexOfSelectedText = selection.anchor.getNode().getTextContent().indexOf(selectedText)

                // NOTE: If the selected text is at the beginning of the paragraph
                //       (index === 0, and the text before the cursor is equal to the selected text or empty)
                atTheBeginning = indexOfSelectedText === 0 || (textBeforeCursor === selectedText || textBeforeCursor.length === 0)
              }

              dispatch(setIsCursorAtStart(atTheBeginning))
            })

            dispatch(setDrilldownLevel('ssml'))
          }
        }
        type='button'
        disabled={!state.editor.isUserActiveInEditor}
      >
        <img className='mr-1 w-4 h-4' src={insertSsmlIcon} /> Insert
      </button>

      <div className={`toolbar-item ml-10 flex items-center ${charCount < 0 ? 'text-red-500' : 'text-gray-500'}`}>
        {charCount}
      </div>

      <button
        className='toolbar-item ml-auto sm:hidden'
        type='button'
        onClick={toggleFullscreen}
      >
        {state.editor.isFullscreen
          ? <img src={fullscreenExitIcon} />
          : <img src={fullscreenEnterIcon} />}
      </button>

      {drilldownHandlers(state.editor.drilldownLevel)}
    </div>
  )
}

export { ToolbarPlugin }
